import { useCallback, useEffect, useState } from 'react';

/** localStorage key holding the rep's opt-in flag. Exported so non-hook
 *  consumers (e.g. legacy `telephony.tsx`) can read it without duplicating
 *  the string literal. */
export const FOCUSED_VIEW_ENABLED_KEY = 'focused-view:enabled';
const STORAGE_KEY = FOCUSED_VIEW_ENABLED_KEY;
const CHANGE_EVENT = 'focused-view:enabled-changed';

function read(): boolean {
	try {
		return window.localStorage.getItem(STORAGE_KEY) === 'true';
	} catch {
		return false;
	}
}

/**
 * Client-side opt-in for the Focused Lead View. Lives in `localStorage` under
 * `focused-view:enabled` until the backend grows a real `coffeePreferences`
 * field for it. Multiple consumers stay in sync via the `storage` event (for
 * cross-tab updates) and a same-tab `focused-view:enabled-changed` custom
 * event fired on write.
 */
export function useFocusedViewEnabled(): [boolean, (next: boolean) => void] {
	const [enabled, setEnabled] = useState<boolean>(read);

	useEffect(() => {
		const sync = () => setEnabled(read());
		window.addEventListener('storage', sync);
		window.addEventListener(CHANGE_EVENT, sync);
		return () => {
			window.removeEventListener('storage', sync);
			window.removeEventListener(CHANGE_EVENT, sync);
		};
	}, []);

	const setFlag = useCallback((next: boolean) => {
		try {
			window.localStorage.setItem(STORAGE_KEY, next ? 'true' : 'false');
		} catch {
			// localStorage unavailable (quota, incognito, etc.) — state change
			// below still updates the in-memory value for this hook.
		}
		setEnabled(next);
		window.dispatchEvent(new Event(CHANGE_EVENT));
	}, []);

	return [enabled, setFlag];
}
